import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { OfflineBanner } from "./components/OfflineBanner";
import { ConfirmHost } from "./components/ConfirmHost";
import Landing from "./pages/Landing";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import Wizard from "./pages/Wizard";
import PhotobookEditor from "./pages/PhotobookEditor";
import WhoPaysWhatPage from "./pages/WhoPaysWhatPage";
import TripDetail from "./pages/TripDetail";
import SharePage from "./pages/SharePage";
import RecapPage from "./pages/RecapPage";
import JournalPage from "./pages/JournalPage";
import ReplyPage from "./pages/ReplyPage";
import AdminPage from "./pages/AdminPage";
import BillingReturn from "./pages/BillingReturn";
import StaysPage from "./pages/StaysPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import UpdatesPage from "./pages/UpdatesPage";
import HelpPage from "./pages/HelpPage";
import RouteLibrary from "./pages/RouteLibrary";
import RouteTemplatePage from "./pages/RouteTemplatePage";
import GaragePage from "./pages/GaragePage";
import { FeedbackWidget } from "./components/FeedbackWidget";
import { PageTracker } from "./components/PageTracker";
import { ErrorBoundary } from "./components/ErrorBoundary";

const RouteBoundary = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <PageTracker />
          <RouteBoundary>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/moto" element={<Navigate to="/?mode=moto" replace />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/wachtwoord-herstellen" element={<ResetPasswordPage />} />
            <Route path="/wat-is-nieuw" element={<UpdatesPage />} />
            <Route path="/hoe-werkt-het" element={<HelpPage />} />
            <Route path="/routes" element={<RouteLibrary />} />
            <Route path="/routes/:slug" element={<RouteTemplatePage />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/nieuwe-trip" element={<Protected><Wizard /></Protected>} />
            <Route path="/overnachtingen" element={<Protected><StaysPage /></Protected>} />
            <Route path="/garage" element={<Protected><GaragePage /></Protected>} />
            <Route path="/trip/:id" element={<Protected><TripDetail /></Protected>} />
            <Route path="/trip/:id/fotoboek" element={<Protected><PhotobookEditor /></Protected>} />
            <Route path="/trip/:id/wie-betaalt-wat" element={<Protected><WhoPaysWhatPage /></Protected>} />
            <Route path="/share/:token" element={<SharePage />} />
            <Route path="/recap/:token" element={<RecapPage />} />
            <Route path="/journal/:token" element={<JournalPage />} />
            <Route path="/reply/:token" element={<ReplyPage />} />
            <Route path="/admin" element={<Protected><AdminPage /></Protected>} />
            <Route path="/billing/return" element={<Protected><BillingReturn /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </RouteBoundary>
        </BrowserRouter>
        <FeedbackWidget />
        <OfflineBanner />
        <ConfirmHost />
        <Toaster position="bottom-center" richColors />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
